"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { ReportsAPI } from "@/lib/api";
import { Report, ReportStatus } from "@/types";
import {
  formatDate,
  formatReportCategory,
  getEntityTypeLabel,
  timeAgo,
} from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ReportStatusBadge } from "@/components/reports/ReportStatusBadge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ReportDetailView } from "@/components/reports/ReportDetailView";
import { Search, Filter } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { toast } from "sonner";

export default function ReportsPage() {
  const mountedRef = useRef(false);
  const fetchInProgress = useRef(false);
  const initialRenderRef = useRef(true);
  const [reports, setReports] = useState<Report[]>([]);
  const [totalReports, setTotalReports] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const limit = 10;

  const [searchParams, setSearchParams] = useState({
    page: 1,
    status: "all" as ReportStatus | "all",
  });

  const fetchReports = useCallback(async () => {
    // Skip if fetch is in progress
    if (fetchInProgress.current) {
      return;
    }

    fetchInProgress.current = true;
    setIsLoading(true);

    try {
      const response = await ReportsAPI.getAll(
        searchParams.page,
        limit,
        searchParams.status === "all" ? undefined : searchParams.status
      );

      if (Array.isArray(response)) {
        setReports(response);
        setTotalReports(response.length);
      } else {
        setReports(response.data || []);
        setTotalReports(response.total || 0);
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
      toast.error("Failed to load reports");
      setReports([]);
      setTotalReports(0);
    } finally {
      setIsLoading(false);
      fetchInProgress.current = false;
    }
  }, [searchParams, limit]);

  // Single effect for initialization and search params changes
  useEffect(() => {
    // Skip the effect if it's the initial render and the component was previously mounted
    if (initialRenderRef.current && mountedRef.current) {
      initialRenderRef.current = false;
      return;
    }

    // Set mounted ref
    mountedRef.current = true;

    // Fetch data
    fetchReports();

    // Cleanup
    return () => {
      fetchInProgress.current = false;
    };
  }, [fetchReports]); // Only depend on fetchReports

  const handleStatusFilterChange = (value: string) => {
    setSearchParams((prev) => ({
      ...prev,
      page: 1, // Reset to first page when filter changes
      status: value as ReportStatus | "all",
    }));
  };

  const handlePageChange = (newPage: number) => {
    setSearchParams((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const totalPages = Math.ceil(totalReports / limit);

  const handleReportClick = (report: Report) => {
    setSelectedReport(report);
    setIsDialogOpen(true);
  };

  const handleReportUpdated = () => {
    setIsDialogOpen(false);
    fetchReports();
  };

  const getStatusOptions = () => {
    const statuses: ReportStatus[] = [
      "pending",
      "processing",
      "resolved",
      "rejected",
      "invalid",
    ];
    return statuses;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Reports</h1>
        <p className="text-muted-foreground">
          Manage and resolve user-submitted reports
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex flex-1 items-center gap-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search reports..."
            className="max-w-sm"
            disabled // This would be implemented in a real app
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select
            value={searchParams.status}
            onValueChange={handleStatusFilterChange}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {getStatusOptions().map((status) => (
                <SelectItem key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Reporter</TableHead>
              <TableHead>Reported</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <div className="h-6 w-12 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-20 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-24 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-24 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-24 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-16 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="h-8 w-16 animate-pulse rounded bg-muted ml-auto" />
                  </TableCell>
                </TableRow>
              ))
            ) : !reports || reports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center">
                  No reports found
                </TableCell>
              </TableRow>
            ) : (
              reports.map((report) => (
                <TableRow key={report.id}>
                  <TableCell>#{report.id}</TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span>{getEntityTypeLabel(report.entity_type)}</span>
                      <span className="text-xs text-muted-foreground">
                        ID: {report.entity_id}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>{formatReportCategory(report.category)}</TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span>{report.reported_by_user.display_name}</span>
                      <span className="text-xs text-muted-foreground">
                        @{report.reported_by_user.user_name}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span>{formatDate(report.created_date)}</span>
                      <span className="text-xs text-muted-foreground">
                        {timeAgo(report.created_date)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <ReportStatusBadge status={report.status} />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleReportClick(report)}
                    >
                      Review
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <Pagination className="mt-4">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (searchParams.page > 1)
                    handlePageChange(searchParams.page - 1);
                }}
                className={
                  searchParams.page === 1
                    ? "pointer-events-none opacity-50"
                    : ""
                }
              />
            </PaginationItem>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum = searchParams.page;
              if (searchParams.page < 3) {
                pageNum = i + 1;
              } else if (searchParams.page > totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = searchParams.page - 2 + i;
              }

              if (pageNum > 0 && pageNum <= totalPages) {
                return (
                  <PaginationItem key={pageNum}>
                    <PaginationLink
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(pageNum);
                      }}
                      isActive={pageNum === searchParams.page}
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              }
              return null;
            })}
            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (searchParams.page < totalPages)
                    handlePageChange(searchParams.page + 1);
                }}
                className={
                  searchParams.page === totalPages
                    ? "pointer-events-none opacity-50"
                    : ""
                }
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Report Details</DialogTitle>
            <DialogDescription>
              Review and manage this reported content
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-6 py-4">
            {selectedReport && (
              <>
                <ReportDetailView
                  report={selectedReport}
                  onReportUpdated={handleReportUpdated}
                />
                <div className="sticky bottom-0 flex justify-end gap-2 pt-4 mt-4 border-t bg-background">
                  <Button
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" form="report-form">
                    Update Report
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
